import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../../../services/useAuth";
import { useTitle } from "../../../hooks/useTitle";
import { useColumnVisibility } from "../../../hooks/useColumnVisibility";
import AppPage from "../../../components/CustomUI/AppPage";
import ListViewOptionsPanel from "../shared/ListViewOptionsPanel";
import CardContainer from "../../../components/CustomUI/CardContainer";
import { Table, Tr, Td } from "../../../components/BaseUI/Table";
import Button from "../../../components/BaseUI/Button";
import IconButton from "../../../components/BaseUI/Button/IconButton";
import Select from "../../../components/BaseUI/Select";
import Input from "../../../components/BaseUI/Input";
import SidebarContainer from "../../../components/CustomUI/SidebarContainer";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import Label from "../../../components/BaseUI/Label";
import Lozenge from "../../../components/BaseUI/Lozenge";
import {
  FiShield,
  FiEdit2,
  FiCheckCircle,
  FiXCircle,
  FiClock,
  FiLayers,
} from "react-icons/fi";
import { withAuth } from "../../../components/HOC/withAuth";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

const LICENSE_STATUS_OPTIONS = [
  { label: "Aktif", value: "active" },
  { label: "Süresi Dolmuş", value: "expired" },
  { label: "Askıya Alınmış", value: "suspended" },
];

const LICENSE_TYPE_OPTIONS = [
  { label: "Deneme", value: "trial" },
  { label: "Temel", value: "basic" },
  { label: "Premium", value: "premium" },
  { label: "Kurumsal", value: "enterprise" },
];

const Licenses = () => {
  useTitle("Rovion MES - Lisans Yönetimi");
  const { getToken, getUser, loading: authLoading } = useAuth();
  const currentUser = getUser();
  const isSystemAdmin = currentUser?.role?.toLowerCase() === "admin";

  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpened, setSidebarOpened] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [toasts, setToasts] = useState([]);

  const [formData, setFormData] = useState({
    licenseType: "trial",
    licenseStatus: "active",
    licenseExpiry: "",
    maxUsers: "",
  });

  const headers = [
    {
      name: "Firma Kodu",
      dataType: "text",
      id: 0,
      customSorter: (a, b) =>
        String(a?.code || "").localeCompare(String(b?.code || "")),
    },
    {
      name: "Firma Adı",
      dataType: "text",
      id: 1,
      customSorter: (a, b) =>
        String(a?.name || "").localeCompare(String(b?.name || "")),
    },
    {
      name: "Lisans Tipi",
      dataType: "text",
      id: 2,
      customSorter: (a, b) =>
        String(a?.licenseType || "").localeCompare(String(b?.licenseType || "")),
    },
    {
      name: "Durum",
      dataType: "text",
      id: 3,
      customSorter: (a, b) =>
        String(a?.licenseStatus || "").localeCompare(
          String(b?.licenseStatus || ""),
        ),
    },
    {
      name: "Bitiş Tarihi",
      dataType: "date",
      id: 4,
      customSorter: (a, b) =>
        new Date(a?.licenseExpiry || 0) - new Date(b?.licenseExpiry || 0),
    },
    {
      name: "Kullanıcı Kotası",
      dataType: "number",
      id: 5,
      customSorter: (a, b) =>
        Number(a?.maxUsers || 0) - Number(b?.maxUsers || 0),
    },
    { name: "İşlemler", id: 6, sortable: false },
  ];

  const { visibleColumns, toggleColumnVisibility, resetToDefault } =
    useColumnVisibility("licenses-list", headers);

  const addToast = (message, type = "info") => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(
      () => setToasts((prev) => prev.filter((t) => t.id !== id)),
      4000,
    );
  };

  useEffect(() => {
    if (isSystemAdmin) {
      fetchCompanies();
    }
  }, [isSystemAdmin]);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const token = getToken();
      const response = await axios.get(`${API_URL}/api/system-companies`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCompanies(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Cariler yüklenemedi:", error);
      addToast("Cariler yüklenemedi", "danger");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (company) => {
    setEditingCompany(company);
    setFormData({
      licenseType: company.licenseType || "trial",
      licenseStatus: company.licenseStatus || "active",
      licenseExpiry: company.licenseExpiry
        ? new Date(company.licenseExpiry).toISOString().split("T")[0]
        : "",
      maxUsers: company.maxUsers || "",
    });
    setSidebarOpened(true);
  };

  const handleExpiryChange = (value) => {
    setFormData((prev) => ({ ...prev, licenseExpiry: value }));
  };

  const handleClose = () => {
    setSidebarOpened(false);
    setEditingCompany(null);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const token = getToken();

      const payload = {
        licenseType: formData.licenseType,
        licenseExpiry: formData.licenseExpiry
          ? new Date(formData.licenseExpiry).toISOString()
          : null,
        maxUsers: formData.maxUsers ? parseInt(formData.maxUsers, 10) : null,
      };

      const response = await axios.put(
        `${API_URL}/api/system-companies/${editingCompany._id}/license`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.success) {
        addToast("Lisans başarıyla güncellendi", "success");
        setSidebarOpened(false);
        setEditingCompany(null);
        fetchCompanies();
      } else {
        addToast(response.data.message || "Lisans güncellenemedi", "danger");
      }
    } catch (error) {
      console.error("Lisans güncelleme hatası:", error);
      addToast(
        error.response?.data?.message || "Lisans güncellenemedi",
        "danger",
      );
    } finally {
      setLoading(false);
    }
  };

  const getStatusAppearance = (status) => {
    switch (status) {
      case "active":
        return "success-subtle";
      case "expired":
        return "danger-subtle";
      case "suspended":
        return "warning-subtle";
      default:
        return "default";
    }
  };

  const getStatusLabel = (status) => {
    const opt = LICENSE_STATUS_OPTIONS.find((o) => o.value === status);
    return opt ? opt.label : status || "Aktif";
  };

  const getTypeLabel = (type) => {
    const opt = LICENSE_TYPE_OPTIONS.find((o) => o.value === type);
    return opt ? opt.label : type || "Deneme";
  };

  const selectedTypeOption = formData.licenseType ? [formData.licenseType] : [];

  if (authLoading) {
    return (
      <AppPage title="Yükleniyor..." loading={true}>
        <CardContainer>
          <div className="text-center p-5">
            <FiShield
              className="spin text-primary"
              size={48}
              style={{ width: "48px", height: "48px" }}
            />
            <p className="mt-2">Yetkilendirme kontrol ediliyor...</p>
          </div>
        </CardContainer>
      </AppPage>
    );
  }

  if (!isSystemAdmin) {
    return (
      <AppPage title="Erişim Engellendi">
        <CardContainer>
          <div className="text-center p-5">
            <FiShield size={64} className="text-danger mb-3" />
            <h3>Bu sayfaya erişim yetkiniz yok</h3>
            <p className="text-muted">
              Bu sayfa yalnızca sistem yöneticileri içindir.
            </p>
          </div>
        </CardContainer>
      </AppPage>
    );
  }

  return (
    <AppPage
      title="Firma Lisans Yönetimi"
      subtitle="Sistemdeki firmaların lisans, kota ve erişim durumlarını yönetin."
      icon={<FiShield />}
    >
      <CardContainer>
        {loading && companies.length === 0 ? (
          <div className="loading-container text-center p-5">
            <FiShield className="spin text-primary" size={48} />
            <p className="mt-2">Cariler yükleniyor...</p>
          </div>
        ) : (
          <>
            <Table
              columns={headers}
              visibleColumns={visibleColumns}
              tableId="licenses-list"
              checkboxSelection={false}
              loading={loading}
              viewOptions={
                <ListViewOptionsPanel
                  columns={headers}
                  visibleColumns={visibleColumns}
                  onToggleColumn={toggleColumnVisibility}
                  onResetColumns={resetToDefault}
                  showApply={false}
                  showReset={false}
                />
              }
            >
              {companies.map((company) => (
                <Tr key={company._id} id={company._id} data={company}>
                  {visibleColumns.includes(0) && (
                    <Td>
                      <code>{company.code}</code>
                    </Td>
                  )}
                  {visibleColumns.includes(1) && (
                    <Td>
                      <strong>{company.name}</strong>
                    </Td>
                  )}
                  {visibleColumns.includes(2) && (
                    <Td>{getTypeLabel(company.licenseType)}</Td>
                  )}
                  {visibleColumns.includes(3) && (
                    <Td>
                      <Lozenge
                        appearance={getStatusAppearance(company.licenseStatus)}
                      >
                        {getStatusLabel(company.licenseStatus)}
                      </Lozenge>
                    </Td>
                  )}
                  {visibleColumns.includes(4) && (
                    <Td>
                      {company.licenseExpiry
                        ? new Date(company.licenseExpiry).toLocaleDateString(
                            "tr-TR",
                          )
                        : "Süresiz"}
                    </Td>
                  )}
                  {visibleColumns.includes(5) && (
                    <Td>
                      {company.maxUsers
                        ? `${company.maxUsers} Kullanıcı`
                        : "Sınırsız"}
                    </Td>
                  )}
                  {visibleColumns.includes(6) && (
                    <Td>
                      <div className="d-flex align-items-center gap-1">
                        <IconButton
                          icon={<FiEdit2 style={{ fontSize: "18px" }} />}
                          appearance="subtle"
                          shape="circle"
                          onClick={() => handleEdit(company)}
                        />
                      </div>
                    </Td>
                  )}
                </Tr>
              ))}
            </Table>

            {companies.length === 0 && (
              <div className="empty-state text-center p-5">
                <FiShield size={64} className="text-muted mb-3" />
                <p>Kayıtlı sistem firması bulunmamaktadır.</p>
              </div>
            )}
          </>
        )}
      </CardContainer>

      <SidebarContainer
        show={sidebarOpened}
        title={
          editingCompany
            ? `Lisans Düzenle: ${editingCompany.name}`
            : "Lisans Yönetimi"
        }
        onClose={handleClose}
      >
        <div className="d-flex flex-column" style={{ height: "100%" }}>
          <div className="flex-grow-1 overflow-auto pe-1">
            <div className="mt-3">
              <Select
                label="Lisans Tipi"
                options={LICENSE_TYPE_OPTIONS}
                selected={selectedTypeOption}
                onSelected={(values) =>
                  setFormData((prev) => ({
                    ...prev,
                    licenseType: values?.[0]?.value || "trial",
                  }))
                }
                unique
              />
            </div>

            <div className="mt-3">
              <Label className="mb-0">Bitiş Tarihi</Label>
              <Input
                type="date"
                value={formData.licenseExpiry}
                onChange={(value) => handleExpiryChange(value)}
              />
              <small className="text-muted d-block mt-1">
                Süresiz yapmak için boş bırakın.
              </small>
            </div>

            <div className="mt-3">
              <Label className="mb-0">
                Maksimum Kullanıcı Sayısı (Opsiyonel)
              </Label>
              <Input
                type="number"
                placeholder="Sınırsız"
                value={formData.maxUsers}
                onChange={(value) =>
                  setFormData((prev) => ({ ...prev, maxUsers: value }))
                }
                min="1"
              />
            </div>
          </div>

          <div className="mt-5 d-flex gap-2 justify-content-end">
            <Button appearance="danger-subtle" onClick={handleClose}>
              İptal
            </Button>
            <Button
              appearance="primary-animated"
              onClick={handleSubmit}
              loading={loading}
            >
              Kaydet
            </Button>
          </div>
        </div>
      </SidebarContainer>

      <ToastContainer>
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            title={toast.message}
            appearance={toast.type}
            onClose={() =>
              setToasts((prev) => prev.filter((t) => t.id !== toast.id))
            }
          />
        ))}
      </ToastContainer>
    </AppPage>
  );
};

export default withAuth(Licenses, ["admin"]);
